import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection, addDoc, Timestamp, onSnapshot, query, orderBy,
} from "firebase/firestore";
import styled, { keyframes, createGlobalStyle, css } from "styled-components";
import { enqueueSnackbar } from "notistack";
import { auth } from "../firebaseConfig";

// ─── Constants ────────────────────────────────────────────────────────────────

const BANKS_GBP = [
  "NATWEST", "BARCLAYS", "LLOYDS", "HSBC", "SANTANDER",
  "HALIFAX", "MONZO", "STARLING", "TSB", "METRO BANK",
];

const BANKS_PKR = [
  "ALLIED", "HBL", "MCB", "UBL", "MEEZAN", "FAYSAL",
  "BANK ALFALAH", "ASKARI", "SILK BANK", "SUMMIT BANK",
];

const CURRENCIES = [
  { code: "GBP", symbol: "£", label: "British Pound", flag: "🇬🇧" },
  { code: "PKR", symbol: "₨", label: "Pakistani Rupee", flag: "🇵🇰" },
];

const REFUND_REASONS = [
  "TICKET VOIDED",
  "FLIGHT CANCELLED",
  "HOTEL CANCELLED",
  "VISA REJECTED",
  "CUSTOMER REQUEST",
  "DUPLICATE BOOKING",
  "DATE CHANGE",
  "PARTIAL REFUND",
  "NIL",
  "OTHER",
];

const getDisplayName = (email) => {
  if (!email) return "User";
  return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const emptyEntry = () => ({
  folderNo: "",
  beneficiary: "",
  bank: "",
  currency: "GBP",
  amountInput: "",
  rate: "",
  reason: "",
  remarks: "",
  date: new Date().toISOString().split("T")[0],
});

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.45; }
`;

const shimmerAnim = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

// ─── Global ───────────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100%;
  background: #f8fafc;
  font-family: 'DM Sans', sans-serif;
  padding: 40px 24px 100px;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const Header = styled.div`
  margin-bottom: 36px;
  animation: ${fadeUp} 0.4s ease both;
`;

const PageLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #f43f5e;
    animation: ${pulse} 2s ease infinite;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.5px;
`;

const PageSub = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

// ─── Stats ────────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.4s ease 0.05s both;

  @media (min-width: 640px) { grid-template-columns: repeat(4, 1fr); }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $color }) => $color};
    border-radius: 12px 12px 0 0;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 6px;
  }

  .stat-value {
    font-size: 22px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.5px;
    line-height: 1;
    font-family: 'DM Mono', monospace;
  }

  .stat-sub {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
  }

  .stat-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 22px;
    opacity: 0.15;
  }
`;

// ─── Currency Selector ────────────────────────────────────────────────────────

const CurrencySelector = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 20px;
  animation: ${fadeUp} 0.4s ease 0.1s both;
`;

const CurrencyLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 12px;
`;

const CurrencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (min-width: 480px) { grid-template-columns: repeat(2, minmax(0, 220px)); }
`;

const CurrencyCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.18s ease;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid ${({ $active }) => $active ? ($active === "GBP" ? "#2563eb" : "#059669") : "#e2e8f0"};
  background: ${({ $active }) =>
    $active === "GBP" ? "#eff6ff" :
    $active === "PKR" ? "#ecfdf5" : "white"};

  .flag { font-size: 24px; }

  .info { text-align: left; }
  .code {
    font-size: 14px;
    font-weight: 600;
    color: ${({ $active }) =>
      $active === "GBP" ? "#2563eb" :
      $active === "PKR" ? "#059669" : "#0f172a"};
  }
  .label {
    font-size: 11px;
    color: #94a3b8;
  }

  .check {
    margin-left: auto;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: ${({ $active }) =>
      $active === "GBP" ? "#2563eb" :
      $active === "PKR" ? "#059669" : "#f1f5f9"};
    display: flex; align-items: center; justify-content: center;
    font-size: 10px;
    color: white;
  }

  &:hover {
    border-color: #94a3b8;
    transform: translateY(-1px);
  }
`;

// ─── Entry Form ───────────────────────────────────────────────────────────────

const EntryCard = styled.div`
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 12px;
  position: relative;
  animation: ${slideIn} 0.25s ease both;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $currency }) => $currency === "GBP" ? "#2563eb" : "#059669"};
    border-radius: 14px 14px 0 0;
  }

  .card-num {
    position: absolute;
    top: -10px;
    left: 20px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.2px;
    color: white;
    background: ${({ $currency }) => $currency === "GBP" ? "#2563eb" : "#059669"};
    padding: 2px 10px;
    border-radius: 20px;
    text-transform: uppercase;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;

  @media (min-width: 640px) { grid-template-columns: repeat(3, 1fr); }
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const accentColor = ({ $currency }) => $currency === "PKR" ? "#059669" : "#2563eb";

const Input = styled.input`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  transition: all 0.15s;
  width: 100%;

  &:focus {
    border-color: ${accentColor};
    background: white;
    box-shadow: 0 0 0 3px ${({ $currency }) => $currency === "PKR" ? "#05996918" : "#2563eb18"};
  }

  &::placeholder { color: #cbd5e1; }
  &:read-only { opacity: 0.65; cursor: not-allowed; }
`;

const StyledSelect = styled.select`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;
  color: #0f172a;
  width: 100%;
  appearance: none;
  padding-right: 30px;
  cursor: pointer;
  transition: all 0.15s;

  &:focus {
    border-color: ${accentColor};
    background-color: white;
    box-shadow: 0 0 0 3px ${({ $currency }) => $currency === "PKR" ? "#05996918" : "#2563eb18"};
  }
`;

// ─── PKR Conversion Banner ────────────────────────────────────────────────────

const ConversionBanner = styled.div`
  background: linear-gradient(135deg, #ecfdf5, #f0fdf4);
  border: 1.5px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  .icon { font-size: 16px; }

  .text {
    font-size: 12px;
    color: #065f46;
    font-weight: 500;
  }

  .amount {
    margin-left: auto;
    font-family: 'DM Mono', monospace;
    font-size: 14px;
    font-weight: 600;
    color: #059669;
  }
`;

// ─── Textarea ─────────────────────────────────────────────────────────────────

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  resize: vertical;
  transition: all 0.15s;

  &:focus {
    border-color: ${accentColor};
    background: white;
    box-shadow: 0 0 0 3px ${({ $currency }) => $currency === "PKR" ? "#05996918" : "#2563eb18"};
  }
  &::placeholder { color: #cbd5e1; }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────

const RemoveBtn = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 7px;
  border: 1px solid #fee2e2;
  background: #fef2f2;
  color: #ef4444;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.12s;
  &:hover { background: #fee2e2; transform: scale(1.05); }
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 18px;
  font-size: 13px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: ${({ $currency }) => $currency === "PKR" ? "#059669" : "#2563eb"};
  background: ${({ $currency }) => $currency === "PKR" ? "#ecfdf5" : "#eff6ff"};
  border: 1.5px dashed ${({ $currency }) => $currency === "PKR" ? "#059669" : "#2563eb"}55;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;
  margin-bottom: 16px;
  transition: all 0.15s;

  &:hover {
    border-style: solid;
    border-color: ${({ $currency }) => $currency === "PKR" ? "#059669" : "#2563eb"};
    background: white;
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: ${({ $currency }) => $currency === "PKR" ? "#059669" : "#2563eb"};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.2px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    filter: brightness(1.07);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${({ $currency }) => $currency === "PKR" ? "#05996944" : "#2563eb44"};
  }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

// ─── Divider ──────────────────────────────────────────────────────────────────

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 36px 0;
`;

// ─── Submitted Requests Table ─────────────────────────────────────────────────

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    font-size: 17px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }

  .count {
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    background: #f1f5f9;
    padding: 3px 10px;
    border-radius: 20px;
  }
`;

const TableWrap = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  overflow-x: auto;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 90px 90px 130px 110px 100px 90px 110px 100px;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  gap: 12px;
  min-width: 860px;

  span {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 90px 90px 130px 110px 100px 90px 110px 100px;
  padding: 13px 16px;
  border-bottom: 0.5px solid #f1f5f9;
  gap: 12px;
  align-items: center;
  transition: background 0.1s;
  min-width: 860px;
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${({ $i }) => Math.min($i * 30, 200)}ms;

  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }

  .cell {
    font-size: 12.5px;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mono {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: #475569;
  }
`;

const CurrencyPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $c }) => $c === "GBP" ? "#eff6ff" : "#ecfdf5"};
  color: ${({ $c }) => $c === "GBP" ? "#2563eb" : "#059669"};
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $s }) => $s === "PENDING" ? "#fffbeb" : "#ecfdf5"};
  color:      ${({ $s }) => $s === "PENDING" ? "#92400e" : "#065f46"};

  &::before {
    content: '';
    width: 5px; height: 5px;
    border-radius: 50%;
    background: ${({ $s }) => $s === "PENDING" ? "#f59e0b" : "#10b981"};
    ${({ $s }) => $s === "PENDING" && css`animation: ${pulse} 1.4s ease infinite;`}
  }
`;

const EmptyState = styled.div`
  padding: 52px 24px;
  text-align: center;
  color: #94a3b8;

  .icon { font-size: 36px; margin-bottom: 10px; opacity: 0.4; }
  .msg  { font-size: 14px; font-weight: 500; color: #64748b; }
  .sub  { font-size: 12px; margin-top: 4px; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function CustomerRefundRequest() {
  const [selectedCurrency, setSelectedCurrency] = useState("GBP");
  const [entries, setEntries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    setUser(auth.currentUser);
  }, []);

  useEffect(() => {
    const q = query(collection(db, "customerRefundRequests"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const mine = all.filter((r) => r.agent === getDisplayName(user?.email));
      setRequests(mine);
    });
    return () => unsub();
  }, [user?.email]);

  const handleSelectCurrency = (code) => {
    setSelectedCurrency(code);
    setEntries([]);
  };

  const handleAddEntry = () => setEntries([...entries, emptyEntry()]);

  const handleRemoveEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const handleChange = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
  };

  const calcGBP = (entry) => {
    const amt = parseFloat(entry.amountInput);
    const rate = parseFloat(entry.rate);
    if (!amt || isNaN(amt)) return null;
    if (selectedCurrency === "PKR") {
      if (!rate || isNaN(rate)) return null;
      return (amt / rate).toFixed(2);
    }
    return amt.toFixed(2);
  };

  const validateEntries = () =>
    entries.every((e) => {
      const base = e.folderNo && e.beneficiary && e.bank && e.amountInput && e.reason && e.date;
      if (selectedCurrency === "PKR") return base && e.rate;
      return base;
    });

  const handleSubmit = async () => {
    if (entries.length === 0) return enqueueSnackbar("Add at least one refund entry", { variant: "warning" });
    if (!validateEntries()) return enqueueSnackbar("Please fill all required fields", { variant: "error" });

    setSubmitting(true);
    try {
      for (const entry of entries) {
        const gbp = calcGBP(entry);
        await addDoc(collection(db, "customerRefundRequests"), {
          agent: getDisplayName(user?.email),
          folderNo: entry.folderNo,
          beneficiary: entry.beneficiary.toUpperCase(),
          bank: entry.bank,
          currency: selectedCurrency,
          amountInput: selectedCurrency === "GBP" ? parseFloat(entry.amountInput) : entry.amountInput,
          amountGBP: parseFloat(gbp || 0),
          rate: selectedCurrency === "PKR" ? entry.rate : "",
          reason: entry.reason,
          remarks: entry.remarks || "",
          date: entry.date,
          status: "PENDING",
          refundedBy: "",
          dateObj: Timestamp.fromDate(new Date()),
          createdAt: Timestamp.now(),
          timestamp: Timestamp.now(),
        });
      }
      enqueueSnackbar(`${entries.length} refund request${entries.length > 1 ? "s" : ""} submitted`, { variant: "success" });
      setEntries([]);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error submitting refund requests", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const totalGBP = requests.reduce((s, r) => s + (parseFloat(r.amountGBP) || 0), 0);
  const pending = requests.filter((r) => r.status === "PENDING").length;
  const refunded = requests.filter((r) => r.status === "REFUNDED").length;

  const bankList = selectedCurrency === "PKR" ? BANKS_PKR : BANKS_GBP;
  const accentCol = selectedCurrency === "PKR" ? "#059669" : "#2563eb";

  return (
    <>
      <GlobalStyle />
      <Page>
        <Inner>

          {/* ── Header ── */}
          <Header>
            <PageLabel>Operations</PageLabel>
            <PageTitle>Refund Requests</PageTitle>
            <PageSub>Submit customer refund requests for processing and approval</PageSub>
          </Header>

          {/* ── Stats ── */}
          <StatsRow>
            <StatCard $color="#f43f5e">
              <div className="stat-label">Total Submitted</div>
              <div className="stat-value">{requests.length}</div>
              <div className="stat-sub">all time</div>
              <div className="stat-icon">📋</div>
            </StatCard>
            <StatCard $color="#f59e0b">
              <div className="stat-label">Pending</div>
              <div className="stat-value">{pending}</div>
              <div className="stat-sub">awaiting approval</div>
              <div className="stat-icon">⏳</div>
            </StatCard>
            <StatCard $color="#10b981">
              <div className="stat-label">Refunded</div>
              <div className="stat-value">{refunded}</div>
              <div className="stat-sub">processed</div>
              <div className="stat-icon">✅</div>
            </StatCard>
            <StatCard $color="#2563eb">
              <div className="stat-label">Total Value</div>
              <div className="stat-value">£{totalGBP.toFixed(2)}</div>
              <div className="stat-sub">GBP equivalent</div>
              <div className="stat-icon">💷</div>
            </StatCard>
          </StatsRow>

          {/* ── Currency Selection ── */}
          <CurrencySelector>
            <CurrencyLabel>Select Refund Currency</CurrencyLabel>
            <CurrencyGrid>
              {CURRENCIES.map((c) => (
                <CurrencyCard
                  key={c.code}
                  $active={selectedCurrency === c.code ? c.code : false}
                  onClick={() => handleSelectCurrency(c.code)}
                >
                  <span className="flag">{c.flag}</span>
                  <div className="info">
                    <div className="code">{c.code}</div>
                    <div className="label">{c.label}</div>
                  </div>
                  <div className="check">
                    {selectedCurrency === c.code ? "✓" : ""}
                  </div>
                </CurrencyCard>
              ))}
            </CurrencyGrid>
          </CurrencySelector>

          {/* ── Entry Cards ── */}
          {entries.map((entry, index) => {
            const gbp = calcGBP(entry);
            return (
              <EntryCard key={index} $currency={selectedCurrency}>
                <div className="card-num">
                  {selectedCurrency === "GBP" ? "£" : "₨"} Entry #{index + 1}
                </div>
                <RemoveBtn onClick={() => handleRemoveEntry(index)}>×</RemoveBtn>

                <FieldGrid style={{ marginTop: 12 }}>
                  <FieldWrap>
                    <FieldLabel>Folder No *</FieldLabel>
                    <Input
                      placeholder="e.g. 1449"
                      value={entry.folderNo}
                      onChange={(e) => handleChange(index, "folderNo", e.target.value)}
                      $currency={selectedCurrency}
                    />
                  </FieldWrap>

                  <FieldWrap>
                    <FieldLabel>Beneficiary *</FieldLabel>
                    <Input
                      placeholder="Full name"
                      value={entry.beneficiary}
                      onChange={(e) => handleChange(index, "beneficiary", e.target.value.toUpperCase())}
                      $currency={selectedCurrency}
                    />
                  </FieldWrap>

                  <FieldWrap>
                    <FieldLabel>Bank *</FieldLabel>
                    <StyledSelect
                      value={entry.bank}
                      onChange={(e) => handleChange(index, "bank", e.target.value)}
                      $currency={selectedCurrency}
                    >
                      <option value="">Select bank</option>
                      {bankList.map((b) => <option key={b}>{b}</option>)}
                    </StyledSelect>
                  </FieldWrap>

                  <FieldWrap>
                    <FieldLabel>
                      Amount ({selectedCurrency === "GBP" ? "£ GBP" : "₨ PKR"}) *
                    </FieldLabel>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={entry.amountInput}
                      onChange={(e) => handleChange(index, "amountInput", e.target.value)}
                      $currency={selectedCurrency}
                    />
                  </FieldWrap>

                  {selectedCurrency === "PKR" && (
                    <FieldWrap>
                      <FieldLabel>Rate (PKR → £) *</FieldLabel>
                      <Input
                        type="number"
                        placeholder="e.g. 389.08"
                        value={entry.rate}
                        onChange={(e) => handleChange(index, "rate", e.target.value)}
                        $currency={selectedCurrency}
                      />
                    </FieldWrap>
                  )}

                  <FieldWrap>
                    <FieldLabel>Date *</FieldLabel>
                    <Input
                      type="date"
                      value={entry.date}
                      onChange={(e) => handleChange(index, "date", e.target.value)}
                      $currency={selectedCurrency}
                    />
                  </FieldWrap>

                  <FieldWrap>
                    <FieldLabel>Reason *</FieldLabel>
                    <StyledSelect
                      value={entry.reason}
                      onChange={(e) => handleChange(index, "reason", e.target.value)}
                      $currency={selectedCurrency}
                    >
                      <option value="">Select reason</option>
                      {REFUND_REASONS.map((r) => <option key={r}>{r}</option>)}
                    </StyledSelect>
                  </FieldWrap>

                  <FieldWrap>
                    <FieldLabel>Agent</FieldLabel>
                    <Input
                      value={getDisplayName(user?.email)}
                      readOnly
                      $currency={selectedCurrency}
                    />
                  </FieldWrap>

                  <FieldWrap style={{ gridColumn: "1 / -1" }}>
                    <FieldLabel>Remarks</FieldLabel>
                    <TextArea
                      placeholder="Any additional notes…"
                      value={entry.remarks}
                      onChange={(e) => handleChange(index, "remarks", e.target.value)}
                      $currency={selectedCurrency}
                      style={{ minHeight: 70 }}
                    />
                  </FieldWrap>
                </FieldGrid>

                {selectedCurrency === "PKR" && gbp && (
                  <ConversionBanner>
                    <span className="icon">🔄</span>
                    <span className="text">
                      ₨{parseFloat(entry.amountInput).toLocaleString()} ÷ {entry.rate} =
                    </span>
                    <span className="amount">£{gbp} GBP</span>
                  </ConversionBanner>
                )}
              </EntryCard>
            );
          })}

          <AddBtn $currency={selectedCurrency} onClick={handleAddEntry}>
            + Add Refund Entry
          </AddBtn>

          {entries.length > 0 && (
            <SubmitBtn $currency={selectedCurrency} onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting…" : `↑ Submit ${entries.length} Refund Request${entries.length > 1 ? "s" : ""}`}
            </SubmitBtn>
          )}

          <Divider />

          {/* ── Submitted Requests Table ── */}
          <SectionTitle>
            <h3>My Submitted Requests</h3>
            <span className="count">{requests.length} total</span>
          </SectionTitle>

          <TableWrap>
            <TableHead>
              <span>Folder</span>
              <span>Currency</span>
              <span>Beneficiary</span>
              <span>Bank</span>
              <span>Amount</span>
              <span>£ GBP</span>
              <span>Reason</span>
              <span>Status</span>
            </TableHead>

            {requests.length === 0 && (
              <EmptyState>
                <div className="icon">📭</div>
                <div className="msg">No refund requests yet</div>
                <div className="sub">Submit a request above to get started</div>
              </EmptyState>
            )}

            {requests.map((req, i) => (
              <TableRow key={req.id} $i={i}>
                <div className="cell mono">#{req.folderNo}</div>
                <div className="cell">
                  <CurrencyPill $c={req.currency}>{req.currency}</CurrencyPill>
                </div>
                <div className="cell">{req.beneficiary}</div>
                <div className="cell">{req.bank}</div>
                <div className="cell mono">
                  {req.currency === "GBP" ? "£" : "₨"}{parseFloat(req.amountInput || 0).toLocaleString()}
                </div>
                <div className="cell mono">£{parseFloat(req.amountGBP || 0).toFixed(2)}</div>
                <div className="cell">{req.reason}</div>
                <div className="cell">
                  <StatusPill $s={req.status}>{req.status}</StatusPill>
                </div>
              </TableRow>
            ))}
          </TableWrap>

        </Inner>
      </Page>
    </>
  );
}